import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";

// IMPORTUJEMY CONFIG (To jest kluczowe!)
import { API_URL } from "./config"; 

import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import EmptyPage from "./pages/EmptyPage";
import ContactPage from "./pages/ContactPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import AdminPanel from "./pages/AdminPanel";
import ModeratorPanel from "./pages/ModeratorPanel";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailsPage from "./pages/OrderDetailsPage";
import OrdersArchivePage from "./pages/OrdersArchivePage";
import NewOrderPage from "./pages/NewOrderPage";
import MagazinePage from "./pages/MagazinePage";
import NewProductPage from "./pages/NewProductPage";
import NewProductNeedAccepted from "./pages/NewProductNeedAccepted";
import PurchasesPage from "./pages/PurchasesPage";
import CompletedPurchasesPage from "./pages/CompletedPurchasesPage";
import DeletedPurchasesPage from "./pages/DeletedPurchasesPage";
import CalendarPage from "./pages/CalendarPage";
import ProjectsPage         from './pages/ProjectsPage';
import NewProjectPage       from './pages/NewProjectPage';
import ProjectDetailsPage   from './pages/ProjectDetailsPage';

// Funkcja do ustawiania ciasteczek
function setCookie(name, value, days) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

// Funkcja do pobierania ciasteczek
function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// Funkcja do usuwania ciasteczek
function eraseCookie(name) {
  document.cookie = name + "=; Max-Age=-99999999; path=/";
}

// Główny komponent aplikacji
function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);

  // Sprawdzanie sesji przy ładowaniu aplikacji
  useEffect(() => {
    const checkSession = async () => {
      try {
        // TU BYŁA ZMIANA: Używamy API_URL z backtickami ` `
        const response = await fetch(`${API_URL}/checkSession`, {
          method: "GET",
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setCurrentUser(data.user);
          localStorage.setItem("user", JSON.stringify(data.user));
        } else {
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            setIsAuthenticated(true);
            setCurrentUser(JSON.parse(storedUser));
          }
        }
      } catch (error) {
        console.error("Błąd weryfikacji sesji:", error);
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setIsAuthenticated(true);
          setCurrentUser(JSON.parse(storedUser));
        }
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Wyświetlanie modala przy pierwszym logowaniu
  useEffect(() => {
    if (isAuthenticated && currentUser && currentUser.first_login) {
      setShowFirstLoginModal(true);
    }
  }, [isAuthenticated, currentUser]);

  // Przełączanie motywu (jasny/ciemny)
  const toggleTheme = () => {
    setTheme((prev) => {
      const newTheme = prev === "light" ? "dark" : "light";
      localStorage.setItem("theme", newTheme);
      return newTheme;
    });
  };

  // Pomijanie zmiany hasła przy pierwszym logowaniu
  const handleSkip = async () => {
    try {
      // TU BYŁA ZMIANA
      const response = await fetch(`${API_URL}/skipFirstLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: currentUser.id }),
      });
      if (!response.ok) throw new Error("Błąd przy pomijaniu");
      setShowFirstLoginModal(false);
      setCurrentUser({ ...currentUser, first_login: false });
      localStorage.setItem("user", JSON.stringify({ ...currentUser, first_login: false }));
    } catch (err) {
      console.error("Błąd przy pomijaniu zmiany hasła:", err);
    }
  };

  // Wyświetlanie ładowania podczas sprawdzania sesji
  if (loading) {
    return <div>Ładowanie...</div>;
  }

  return (
    <Router>
      <Layout
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthenticated={setIsAuthenticated}
        theme={theme}
        toggleTheme={toggleTheme}
      />
      {showFirstLoginModal && (
        <FirstLoginModal
          userId={currentUser.id}
          onClose={() => setShowFirstLoginModal(false)}
          onSkip={handleSkip}
          setCurrentUser={setCurrentUser}
        />
      )}
    </Router>
  );
}

// Komponent układu strony
function Layout({
  isAuthenticated,
  currentUser,
  setCurrentUser,
  setIsAuthenticated,
  theme,
  toggleTheme,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isContactPage = location.pathname === "/kontakt";
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);

  // Zamykanie menu użytkownika po kliknięciu poza nim
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowUserMenu(false);
    }
  };

  useEffect(() => {
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  const goToProfile = () => {
    navigate("/profile");
    setShowUserMenu(false);
  };

  const goToSettings = () => {
    navigate("/settings");
    setShowUserMenu(false);
  };

  // Wylogowanie użytkownika
  const handleLogout = async () => {
    try {
      // TU BYŁA ZMIANA
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Błąd wylogowania:", err);
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem("user");
    eraseCookie("userSession");
    navigate("/login");
  };

  return (
    <div className={`app ${theme}`}>
      <Header
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
        onLogout={handleLogout}
        toggleTheme={toggleTheme}
        theme={theme}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        goToProfile={goToProfile}
        goToSettings={goToSettings}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage isAuthenticated={isAuthenticated} />} />
          <Route
            path="/login"
            element={
              <LoginPage
                onLogin={(user) => {
                  setIsAuthenticated(true);
                  setCurrentUser(user);
                  localStorage.setItem("user", JSON.stringify(user));
                }}
              />
            }
          />
          <Route path="/kontakt" element={<ContactPage />} />
          {isAuthenticated && (
            <>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/admin"
                element={
                  currentUser && currentUser.role === "Admin" ? (
                    <AdminPanel />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route path="/projekty"            element={<ProjectsPage />} />
              <Route path="/projekty/nowy"       element={
                 <NewProjectPage currentUser={currentUser} />
              } />
              <Route path="/projekty/:id"        element={<ProjectDetailsPage />} />
              <Route path="/zlecenia" element={<OrdersPage />} />
              <Route path="/zlecenia/:id" element={<OrderDetailsPage />} />
              <Route path="/zlecenia/archiwum" element={<OrdersArchivePage />} />
              <Route path="/zlecenia/nowe" element={<NewOrderPage currentUser={currentUser} />} />
              <Route path="/magazyn" element={<MagazinePage />} />
              <Route path="/magazyn/dodaj" element={<NewProductPage currentUser={currentUser} />} />
              <Route path="/magazyn/nowe" element={<NewProductNeedAccepted currentUser={currentUser} />} />
              <Route path="/zakupy" element={<PurchasesPage />} />
              <Route path="/zakupy/zrealizowane" element={<CompletedPurchasesPage />} />
              <Route path="/zakupy/odrzucone" element={<DeletedPurchasesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage currentUser={currentUser} />} />
              <Route path="/kalendarz" element={<CalendarPage />} />
              <Route
                path="/moderator"
                element={
                  currentUser && currentUser.role === "Moderator" ? (
                    <ModeratorPanel />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
            </>
          )}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

// Modal do zmiany hasła przy pierwszym logowaniu
function FirstLoginModal({ userId, onClose, onSkip, setCurrentUser }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  // Obsługa zmiany hasła
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Hasła nie są takie same");
      return;
    }
    try {
      // TU BYŁA ZMIANA
      const response = await fetch(`${API_URL}/changePassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId, currentPassword: "", newPassword }),
      });
      if (!response.ok) throw new Error("Błąd przy zmianie hasła");
      setCurrentUser((prev) => {
        const updatedUser = { ...prev, first_login: false };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return updatedUser;
      });
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Zmień swoje hasło:</h3>
        <p>Nie zwlekaj, zmień swoje hasło na bezpieczne już teraz!</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="Podaj hasło"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Powtórz hasło"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          {error && <p style={{ color: "red" }}>{error}</p>}
          <div className="modal-buttons">
            <button type="submit" className="btn-submit">
              Zapisz Nowe Hasło
            </button>
            <button type="button" className="btn-submit" onClick={onSkip}>
              Pomiń
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;